import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Stack,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import StoreIcon from '@mui/icons-material/Store';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PaymentsIcon from '@mui/icons-material/Payments';

import apiService from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { MyApplicationDetail } from '../types';
import { calcTotalPayment } from '../utils/salaryUtils';

const formatShiftDate = (yyyymmdd: string): string => {
  // YYYY-MM-DD → M月D日(曜)
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  if (!y || !m || !d) return yyyymmdd;
  const date = new Date(y, m - 1, d);
  const weekday = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];
  return `${m}月${d}日(${weekday})`;
};

const MyApplicationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [applications, setApplications] = useState<MyApplicationDetail[]>([]);

  const fetchApplications = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getMyApplicationsWithDetails(userId);
      if (res.success && res.data) {
        setApplications(res.data);
      } else {
        setError(res.error || 'シフト予定表の取得に失敗しました');
      }
    } catch (err) {
      setError('シフト予定表の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  return (
    <Box sx={{ pt: 0, px: 0, pb: 2, m: 0 }}>
      {/* ページヘッダー */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        px: 1,
        pt: 1.5,
        pb: 0.5,
        minHeight: 48,
      }}>
        <IconButton onClick={() => navigate('/mypage')} size="small" sx={{ color: '#3c3c3c' }}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        <Typography sx={{
          flex: 1,
          fontWeight: 700,
          fontSize: '16px',
          color: '#3c3c3c',
          pl: 0.5,
        }}>
          シフト予定表
        </Typography>
      </Box>

      <Box sx={{ px: 2, pt: 1 }}>
        <Typography variant="caption" sx={{ color: '#888' }}>
          今日以降のシフトが表示されます
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography sx={{ textAlign: 'center', color: 'error.main', my: 4 }}>
            {error}
          </Typography>
        ) : applications.length === 0 ? (
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', my: 4 }}>
            今日以降のシフトはありません
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {applications.map((app) => (
              <Card key={app.id} sx={{ border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {/* 店舗名 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <StoreIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>
                      {app.store_name}
                    </Typography>
                  </Box>

                  {/* 店舗住所 */}
                  {app.store_address && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, color: '#555' }}>
                      <LocationOnIcon sx={{ mr: 1, fontSize: 18, color: '#888', mt: '2px' }} />
                      <Typography sx={{ fontSize: '13px' }}>
                        {app.store_address}
                      </Typography>
                    </Box>
                  )}

                  {/* シフト日付 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, color: '#555' }}>
                    <EventIcon sx={{ mr: 1, fontSize: 18, color: '#888' }} />
                    <Typography sx={{ fontSize: '14px' }}>
                      {formatShiftDate(app.shift_date)}
                    </Typography>
                  </Box>

                  {/* 開始/終了時刻 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, color: '#555' }}>
                    <AccessTimeIcon sx={{ mr: 1, fontSize: 18, color: '#888' }} />
                    <Typography sx={{ fontSize: '14px' }}>
                      {app.start_time} 〜 {app.end_time}
                    </Typography>
                  </Box>

                  {/* 職種 */}
                  {app.position && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, color: '#555' }}>
                      <WorkOutlineIcon sx={{ mr: 1, fontSize: 18, color: '#888' }} />
                      <Typography sx={{ fontSize: '14px' }}>
                        {app.position}
                      </Typography>
                    </Box>
                  )}

                  {/* 時給 / 報酬合計 */}
                  {app.salary_amount != null && (() => {
                    const totalPayment = calcTotalPayment(
                      app.salary_amount,
                      app.salary_type,
                      app.start_time,
                      app.end_time,
                      app.break_duration_minutes ?? 0,
                      app.salary_allowance,
                      app.salary_transport
                    );
                    const isHourly = !app.salary_type || app.salary_type === 'hourly';
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: '#555' }}>
                        <PaymentsIcon sx={{ mr: 1, fontSize: 18, color: '#888' }} />
                        <Typography sx={{ fontSize: '14px' }}>
                          {isHourly ? `時給 ¥${app.salary_amount.toLocaleString()}` : `¥${app.salary_amount.toLocaleString()}`}
                          {totalPayment !== null && (
                            <Typography component="span" sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a', ml: 1 }}>
                              （合計 ¥{totalPayment.toLocaleString()}）
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    );
                  })()}
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default MyApplicationsPage;
